import { useState } from "react";
import { useNavigate, useLoaderData } from "react-router-dom";
import { createEvent, updateEvent } from "../../../api/cms";

const inputClass = "border border-gray-300 rounded px-3 py-2 font-normal focus:outline-none focus:ring-2 focus:ring-bahyellow w-full";
const textareaClass = `${inputClass} font-mono text-xs`;

const Field = ({ label, hint, children }) => (
  <div className="flex flex-col gap-1">
    <label className="text-sm font-medium">{label}</label>
    {hint && <span className="text-xs text-gray-400">{hint}</span>}
    {children}
  </div>
);

const Section = ({ title, children }) => (
  <div className="border-t pt-5 mt-2">
    <h2 className="font-semibold text-base mb-4">{title}</h2>
    <div className="flex flex-col gap-4">{children}</div>
  </div>
);

// Reusable component for arrays of objects with named fields
const ObjectArrayField = ({ label, items, fields, onChange, newItem }) => {
  const add = () => onChange([...items, { ...newItem }]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const setField = (i, key, value) => {
    const next = [...items];
    next[i] = { ...next[i], [key]: value };
    onChange(next);
  };

  return (
    <div className="flex flex-col gap-2">
      <span className="text-sm font-medium">{label}</span>
      {items.map((item, i) => (
        <div key={i} className="border border-gray-200 rounded p-3 flex flex-col gap-2 bg-gray-50">
          {fields.map(({ key, label: fl, type = "text", rows }) =>
            type === "textarea" ? (
              <label key={key} className="flex flex-col gap-1 text-xs font-medium">
                {fl}
                <textarea className={textareaClass} value={item[key] || ""} onChange={(e) => setField(i, key, e.target.value)} rows={rows || 3} />
              </label>
            ) : (
              <label key={key} className="flex flex-col gap-1 text-xs font-medium">
                {fl}
                <input className={inputClass} type={type} value={item[key] || ""} onChange={(e) => setField(i, key, e.target.value)} />
              </label>
            )
          )}
          <button type="button" onClick={() => remove(i)} className="self-end text-xs text-red-400 hover:text-red-600">Remove</button>
        </div>
      ))}
      <button type="button" onClick={add} className="self-start text-blue-600 hover:underline text-xs">+ Add {label.toLowerCase().replace(/s$/, "")}</button>
    </div>
  );
};

const StringArrayField = ({ label, items, onChange }) => {
  const add = () => onChange([...items, ""]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const setItem = (i, value) => { const next = [...items]; next[i] = value; onChange(next); };

  return (
    <div className="flex flex-col gap-2">
      <span className="text-sm font-medium">{label}</span>
      {items.map((item, i) => (
        <div key={i} className="flex gap-2">
          <input className={`${inputClass} flex-1`} value={item} onChange={(e) => setItem(i, e.target.value)} />
          <button type="button" onClick={() => remove(i)} className="text-red-400 hover:text-red-600 px-2">✕</button>
        </div>
      ))}
      <button type="button" onClick={add} className="self-start text-blue-600 hover:underline text-xs">+ Add</button>
    </div>
  );
};

const toDatetimeLocal = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toISOString().slice(0, 16); } catch { return ""; }
};

export default function AdminEventForm() {
  const existing = useLoaderData();
  const navigate = useNavigate();

  const isRange = !!(existing?.dateTimeFrom);

  const [dateMode, setDateMode] = useState(isRange ? "range" : "single");
  const [form, setForm] = useState({
    title: "",
    location: "",
    summary: "",
    banner: "",
    description: "",
    agendaIntro: "",
    targetAudience: "",
    externalUrl: "",
    ...existing,
    dateTime: toDatetimeLocal(existing?.dateTime),
    dateTimeFrom: toDatetimeLocal(existing?.dateTimeFrom),
    dateTimeTo: toDatetimeLocal(existing?.dateTimeTo),
    agenda: existing?.agenda || [],
    speakers: Array.isArray(existing?.speakers) ? existing.speakers : [],
    organizers: existing?.organizers || [],
    partners: existing?.partners || [],
    sponsors: existing?.sponsors || [],
    videos: existing?.videos || [],
    photos: existing?.photos || [],
  });
  const [speakersMode, setSpeakersMode] = useState(
    existing?.speakers && !Array.isArray(existing.speakers) ? "html" : "array"
  );
  const [speakersHtml, setSpeakersHtml] = useState(
    existing?.speakers && !Array.isArray(existing.speakers) ? existing.speakers : ""
  );

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  const setArr = (field) => (value) => setForm((f) => ({ ...f, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload = {
        ...form,
        speakers: speakersMode === "html" ? speakersHtml : form.speakers.filter((s) => s.name),
        photos: form.photos.filter(Boolean),
        videos: form.videos.filter(Boolean),
      };
      // Date handling
      if (dateMode === "single") {
        payload.dateTime = form.dateTime ? new Date(form.dateTime).toISOString() : null;
        delete payload.dateTimeFrom;
        delete payload.dateTimeTo;
      } else {
        payload.dateTimeFrom = form.dateTimeFrom ? new Date(form.dateTimeFrom).toISOString() : null;
        payload.dateTimeTo = form.dateTimeTo ? new Date(form.dateTimeTo).toISOString() : null;
        delete payload.dateTime;
      }

      if (existing?.id) {
        await updateEvent(existing.id, payload);
      } else {
        await createEvent(payload);
      }
      navigate("/admin/events");
    } catch {
      setError("Failed to save. Check your API key and try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl">
      <h1 className="text-2xl font-bold mb-6">{existing?.id ? "Edit event" : "Add event"}</h1>
      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6 flex flex-col gap-5">

        {/* Basic info */}
        <Field label="Title">
          <input className={inputClass} value={form.title} onChange={set("title")} required />
        </Field>
        <Field label="Location">
          <input className={inputClass} value={form.location} onChange={set("location")} />
        </Field>
        <Field label="Summary">
          <textarea className={inputClass} value={form.summary} onChange={set("summary")} rows={2} />
        </Field>
        <Field label="Banner filename" hint="Filename only, e.g. banner.jpg — stored in /img/events/{id}/">
          <input className={inputClass} value={form.banner} onChange={set("banner")} />
        </Field>
        <Field label="External URL" hint="Optional — links to an external event page instead of detail page">
          <input className={inputClass} value={form.externalUrl} onChange={set("externalUrl")} type="url" />
        </Field>

        {/* Date */}
        <Section title="Date & time">
          <div className="flex gap-4 text-sm">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" checked={dateMode === "single"} onChange={() => setDateMode("single")} className="accent-bahyellow" /> Single date
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" checked={dateMode === "range"} onChange={() => setDateMode("range")} className="accent-bahyellow" /> Date range
            </label>
          </div>
          {dateMode === "single" ? (
            <Field label="Date & time">
              <input className={inputClass} type="datetime-local" value={form.dateTime} onChange={set("dateTime")} required />
            </Field>
          ) : (
            <div className="flex gap-4">
              <Field label="From">
                <input className={inputClass} type="datetime-local" value={form.dateTimeFrom} onChange={set("dateTimeFrom")} required />
              </Field>
              <Field label="To">
                <input className={inputClass} type="datetime-local" value={form.dateTimeTo} onChange={set("dateTimeTo")} required />
              </Field>
            </div>
          )}
        </Section>

        {/* Content */}
        <Section title="Content">
          <Field label="Description (HTML)">
            <textarea className={textareaClass} value={form.description} onChange={set("description")} rows={8} />
          </Field>
          <Field label="Target audience (HTML)">
            <textarea className={textareaClass} value={form.targetAudience} onChange={set("targetAudience")} rows={4} />
          </Field>
        </Section>

        {/* Agenda */}
        <Section title="Agenda">
          <Field label="Agenda intro (HTML)">
            <textarea className={textareaClass} value={form.agendaIntro} onChange={set("agendaIntro")} rows={3} />
          </Field>
          <ObjectArrayField
            label="Agenda items"
            items={form.agenda}
            onChange={setArr("agenda")}
            newItem={{ title: "", dateTime: "", description: "", link: "" }}
            fields={[
              { key: "title", label: "Title" },
              { key: "dateTime", label: "Date & time", type: "datetime-local" },
              { key: "description", label: "Description (HTML)", type: "textarea" },
              { key: "link", label: "Link (optional)", type: "url" },
            ]}
          />
        </Section>

        {/* Speakers */}
        <Section title="Speakers">
          <div className="flex gap-4 text-sm">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" checked={speakersMode === "array"} onChange={() => setSpeakersMode("array")} className="accent-bahyellow" /> Structured list
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" checked={speakersMode === "html"} onChange={() => setSpeakersMode("html")} className="accent-bahyellow" /> Raw HTML
            </label>
          </div>
          {speakersMode === "html" ? (
            <Field label="Speakers HTML">
              <textarea className={textareaClass} value={speakersHtml} onChange={(e) => setSpeakersHtml(e.target.value)} rows={6} />
            </Field>
          ) : (
            <ObjectArrayField
              label="Speakers"
              items={form.speakers}
              onChange={setArr("speakers")}
              newItem={{ name: "", photo: "", profileURL: "", position: "", bio: "" }}
              fields={[
                { key: "name", label: "Name" },
                { key: "photo", label: "Photo filename (in /img/events/{id}/speakers/)" },
                { key: "profileURL", label: "Profile URL", type: "url" },
                { key: "position", label: "Position" },
                { key: "bio", label: "Bio (HTML)", type: "textarea" },
              ]}
            />
          )}
        </Section>

        {/* Media */}
        <Section title="Media">
          <StringArrayField label="Photos (filenames, e.g. photo.jpg)" items={form.photos} onChange={setArr("photos")} />
          <StringArrayField label="Video URLs (YouTube embed URLs)" items={form.videos} onChange={setArr("videos")} />
        </Section>

        {/* Organizers / Partners / Sponsors */}
        <Section title="Organizers, partners & sponsors">
          <ObjectArrayField
            label="Organizers"
            items={form.organizers}
            onChange={setArr("organizers")}
            newItem={{ name: "", link: "", logo: "" }}
            fields={[
              { key: "name", label: "Name" },
              { key: "link", label: "Website URL", type: "url" },
              { key: "logo", label: "Logo URL" },
            ]}
          />
          <ObjectArrayField
            label="Partners"
            items={form.partners}
            onChange={setArr("partners")}
            newItem={{ name: "", link: "", logo: "" }}
            fields={[
              { key: "name", label: "Name" },
              { key: "link", label: "Website URL", type: "url" },
              { key: "logo", label: "Logo URL" },
            ]}
          />
          <ObjectArrayField
            label="Sponsors"
            items={form.sponsors}
            onChange={setArr("sponsors")}
            newItem={{ name: "", link: "", logo: "" }}
            fields={[
              { key: "name", label: "Name" },
              { key: "link", label: "Website URL", type: "url" },
              { key: "logo", label: "Logo URL" },
            ]}
          />
        </Section>

        {error && <p className="text-red-500 text-sm">{error}</p>}
        <div className="flex gap-3 mt-2">
          <button type="submit" disabled={saving} className="bg-bahyellow rounded px-4 py-2 text-sm font-semibold hover:opacity-90 disabled:opacity-50">
            {saving ? "Saving…" : "Save"}
          </button>
          <button type="button" onClick={() => navigate("/admin/events")} className="rounded px-4 py-2 text-sm border border-gray-300 hover:bg-gray-50">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
